// pages/whatsapp/WhatsAppOnboarding.jsx
import React, { useState, useEffect } from "react";
import axios from "../../../utils/axios";
import { useNavigate } from "react-router-dom";
import {
  FiCheck,
  FiChevronLeft,
  FiAlertCircle,
  FiPhone,
  FiShield,
  FiZap,
} from "react-icons/fi";

// ── Success Screen ────────────────────────────────────────────────────────────
function SuccessScreen({ data, onGoToDashboard }) {
  return (
    <div className="text-center space-y-6 py-6">
      <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <FiCheck size={48} className="text-green-500" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-800">
          WhatsApp Connected! 🎉
        </h2>
        <p className="text-gray-500 mt-2">
          Your WhatsApp Business Account is now connected
        </p>
      </div>
      <div className="bg-gray-50 rounded-xl p-4 text-left space-y-3 max-w-sm mx-auto">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Business Name:</span>
          <span className="font-semibold text-gray-800">
            {data?.verifiedName || "—"}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Phone Number:</span>
          <span className="font-semibold text-green-600">
            {data?.displayPhone || "—"}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">WABA ID:</span>
          <span className="font-mono text-xs text-gray-600">
            {data?.wabaId || "—"}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Phone Number ID:</span>
          <span className="font-mono text-xs text-gray-600">
            {data?.phoneNumberId || "—"}
          </span>
        </div>
      </div>
      <button
        onClick={onGoToDashboard}
        className="px-8 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors"
      >
        Go to Dashboard →
      </button>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function WhatsAppOnboarding() {
  const navigate = useNavigate();
  const [appId, setAppId] = useState("");
  const [configId, setConfigId] = useState("");
  const [sdkReady, setSdkReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // ── Step 1: Backend se APP_ID aur CONFIG_ID fetch karo ────────────────────
  // Ye values .env mein hain — frontend pe hardcode nahi karte
  // Security ke liye backend se laate hain
  useEffect(() => {
    axios
      .get("/api/onboarding/config")
      .then((res) => {
        if (res.data.success) {
          setAppId(res.data.data.appId);
          setConfigId(res.data.data.configId);
        }
      })
      .catch((err) => {
        console.error("Config fetch error:", err);
        setErrorMsg("Failed to load configuration. Please refresh.");
      });
  }, []);

  // ── Step 2: Facebook SDK load karo jab APP_ID mile ────────────────────────
  // Meta ka recommended way — fbAsyncInit PEHLE define karo, PHIR script load karo
  useEffect(() => {
    if (!appId) return;

    const initFB = () => {
      window.FB.init({
        appId: appId,
        autoLogAppEvents: true,
        xfbml: true,
        version: "v19.0",
      });
      setSdkReady(true);
      console.log("✅ Facebook SDK ready, appId:", appId);
    };

    // Agar SDK pehle se load hai
    if (window.FB) {
      initFB();
      return;
    }

    // IMPORTANT: fbAsyncInit PEHLE define karo — SDK load hone ke baad ye call hoga
    window.fbAsyncInit = initFB;

    // Agar script already hai toh dobara mat daalo
    if (document.getElementById("fb-sdk-script")) return;

    // Meta ka recommended IIFE pattern
    (function (d, s, id) {
      var js,
        fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      js.onerror = () =>
        setErrorMsg("Failed to load Facebook SDK. Disable ad blocker & retry.");
      fjs.parentNode.insertBefore(js, fjs);
    })(document, "script", "fb-sdk-script");
  }, [appId]);

  // ── Step 3: Embedded Signup button click ─────────────────────────────────
  // Ye Meta ka official popup hai
  // User → Facebook login → WhatsApp number select → OTP verify
  // Meta → Code deta hai → Backend se token exchange hota hai
  const handleEmbeddedSignup = () => {
    if (!sdkReady || !window.FB) {
      setErrorMsg("Facebook SDK not loaded yet. Please wait or refresh.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    window.FB.login(
      function (response) {
        console.log("FB login response:", JSON.stringify(response));

        if (response.authResponse?.code) {
          // ✅ Meta ne code diya → backend ko bhejo
          console.log("✅ Code received from Meta");
          handleTokenExchange(response.authResponse.code);
        } else if (response.status === "not_authorized") {
          setErrorMsg("You cancelled the process. Please try again.");
          setLoading(false);
        } else {
          setErrorMsg("Facebook login failed. Please try again.");
          setLoading(false);
        }
      },
      {
        config_id: configId, // Embedded Signup config
        response_type: "code", // Secure — token nahi, code milega
        override_default_response_type: true,
        extras: { sessionInfoVersion: 2 }, // WABA session info ke liye
      },
    );
  };

  // ── Step 4: Code → Token → WABA ID + Phone Number ID ─────────────────────
  // Backend ko code bhejte hain
  // Backend APP_SECRET se token exchange karta hai
  // Phir WABA ID aur Phone Number ID nikalta hai
  // Sab DB mein save hota hai
  const handleTokenExchange = async (code) => {
    setExchanging(true);
    try {
      const res = await axios.post("/api/onboarding/exchange-token", { code });
      if (res.data.success) {
        console.log("✅ Onboarding complete:", res.data.data);
        setSuccessData(res.data.data);
        setSuccess(true);
      } else {
        setErrorMsg(res.data.error || "Connection failed. Please try again.");
      }
    } catch (err) {
      console.error("Token exchange error:", err.response?.data);
      setErrorMsg(
        err.response?.data?.error || "Connection failed. Please try again.",
      );
    } finally {
      setLoading(false);
      setExchanging(false);
    }
  };

  // ── Success Screen ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-lg">
          <SuccessScreen
            data={successData}
            onGoToDashboard={() => navigate("/whatsapp/templates")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 w-full max-w-lg">
        {/* Header */}
        <div className="border-b border-gray-100 px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm"
          >
            <FiChevronLeft size={16} /> Back
          </button>
          <span className="text-gray-300">|</span>
          <h1 className="text-lg font-bold text-gray-800">
            WhatsApp Onboarding
          </h1>
        </div>

        <div className="p-8 space-y-6">
          {/* Meta Logo */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-4xl font-bold text-blue-600">∞</span>
              <span className="text-3xl font-bold text-gray-800">Meta</span>
            </div>
            <p className="text-sm text-gray-500">
              Connect your WhatsApp Business Account
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-3">
            {[
              {
                icon: FiPhone,
                label: "Any Number",
                desc: "Use your business number",
              },
              { icon: FiShield, label: "Secure", desc: "Official Meta API" },
              { icon: FiZap, label: "Instant", desc: "Connect in minutes" },
            ].map((item) => (
              <div
                key={item.label}
                className="text-center p-3 bg-gray-50 rounded-xl"
              >
                <item.icon size={20} className="text-green-500 mx-auto mb-1" />
                <p className="text-xs font-semibold text-gray-700">
                  {item.label}
                </p>
                <p className="text-[10px] text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Steps */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-blue-700 mb-3">
              What will happen:
            </p>
            {[
              "Facebook login popup khulega",
              "Apne Facebook account se login karo",
              "WhatsApp Business number select/verify karo",
              "Allow permissions click karo",
              "Done! Number connected ho jaayega ✅",
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {i + 1}
                </div>
                <span className="text-xs text-blue-700">{step}</span>
              </div>
            ))}
          </div>

          {/* Important Note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
            <FiAlertCircle
              size={14}
              className="text-amber-500 flex-shrink-0 mt-0.5"
            />
            <div className="text-xs text-amber-700 space-y-0.5">
              <p className="font-semibold">Important:</p>
              <p>• Number should NOT be active on personal WhatsApp</p>
              <p>• You need admin access to Facebook Business Manager</p>
              <p>• Keep your phone ready for OTP</p>
            </div>
          </div>

          {/* SDK Status */}
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full transition-all ${sdkReady ? "bg-green-500" : "bg-yellow-400 animate-pulse"}`}
            />
            <span className="text-xs text-gray-400">
              {sdkReady ? "Facebook SDK ready ✓" : "Loading Facebook SDK..."}
            </span>
          </div>

          {/* Error */}
          {errorMsg && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <FiAlertCircle
                size={14}
                className="text-red-500 flex-shrink-0 mt-0.5"
              />
              <p className="text-sm text-red-600">{errorMsg}</p>
            </div>
          )}

          {/* Exchanging loader */}
          {exchanging && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 flex-shrink-0" />
              <p className="text-sm text-blue-600">
                Connecting your WhatsApp Business Account...
              </p>
            </div>
          )}

          {/* Main Button */}
          <button
            type="button"
            onClick={handleEmbeddedSignup}
            disabled={!sdkReady || loading || exchanging}
            className="w-full flex items-center justify-center gap-3 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-base font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-200"
          >
            {loading || exchanging ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                {exchanging ? "Connecting..." : "Opening Facebook..."}
              </>
            ) : (
              <>
                <span className="text-xl font-serif">f</span>
                Continue Embedded Signup
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
